import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/firebase';

interface License {
  id: string;
  licenseKey: string;
  username: string | null;
  email: string;
  isActive: boolean;
  maxActivations: number;
  totalActivations: number;
  activeActivations: number;
  createdAt: any;
  expiresAt: any;
  notes: string;
  isAssigned?: boolean;
  assignedAt?: any;
  assignedBy?: string;
}

interface LicensesResponse {
  success: boolean;
  data: License[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

interface CreateLicenseForm {
  username: string;
  email: string;
  maxActivations: number;
  expiresAt: string;
  notes: string;
}

interface EditLicenseForm {
  username: string;
  email: string;
  maxActivations: number;
  expiresAt: string;
  notes: string;
  isActive: boolean;
}

export function Licenses() {
  const { currentUser } = useAuth();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [createForm, setCreateForm] = useState<CreateLicenseForm>({
    username: '',
    email: '',
    maxActivations: 1,
    expiresAt: '',
    notes: ''
  });

  const [inviteForm, setInviteForm] = useState({
    email: '',
    maxActivations: 1,
    expiresAt: '',
    notes: ''
  });

  const [editForm, setEditForm] = useState<EditLicenseForm>({
    username: '',
    email: '',
    maxActivations: 1,
    expiresAt: '',
    notes: '',
    isActive: true
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    expired: ''
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/licenses?page=1&limit=50`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: LicensesResponse = await response.json();

      if (data.success) {
        setLicenses(data.data);
      } else {
        throw new Error('Failed to fetch licenses');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch licenses');
      console.error('Error fetching licenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const createLicense = async () => {
    setCreating(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/licenses`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          username: createForm.username.trim() || null,
          email: createForm.email,
          maxActivations: createForm.maxActivations,
          expiresAt: createForm.expiresAt || null,
          notes: createForm.notes
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowCreateModal(false);
        setCreateForm({
          username: '',
          email: '',
          maxActivations: 1,
          expiresAt: '',
          notes: ''
        });
        await fetchLicenses();
        showMessage('success', `Licentie succesvol aangemaakt! Sleutel: ${data.data.licenseKey}`);
      } else {
        throw new Error(data.error || 'Failed to create license');
      }
    } catch (err) {
      showMessage('error', 'Fout bij aanmaken van licentie: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setCreating(false);
    }
  };

  const createInvitation = async () => {
    if (!inviteForm.email.trim()) {
      showMessage('error', 'Email is verplicht voor uitnodiging');
      return;
    }

    setInviting(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/licenses/invite`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: inviteForm.email.trim(),
          maxActivations: inviteForm.maxActivations,
          expiresAt: inviteForm.expiresAt || null,
          notes: inviteForm.notes
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowInviteModal(false);
        setInviteForm({
          email: '',
          maxActivations: 1,
          expiresAt: '',
          notes: ''
        });
        await fetchLicenses();
        showMessage('success', `Uitnodiging succesvol verzonden naar ${inviteForm.email}! Licentie sleutel: ${data.data.licenseKey}`);
      } else {
        throw new Error(data.error || 'Failed to send invitation');
      }
    } catch (err) {
      showMessage('error', 'Fout bij versturen uitnodiging: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setInviting(false);
    }
  };

  const updateLicense = async () => {
    if (!editingLicense) {
      showMessage('error', 'Geen licentie geselecteerd voor bewerking');
      return;
    }

    setUpdating(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/licenses/${editingLicense.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          username: editForm.username.trim() || null,
          email: editForm.email,
          maxActivations: editForm.maxActivations,
          expiresAt: editForm.expiresAt || null,
          notes: editForm.notes,
          isActive: editForm.isActive
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowEditModal(false);
        setEditingLicense(null);
        await fetchLicenses();
        showMessage('success', 'Licentie succesvol bijgewerkt');
      } else {
        throw new Error(data.error || 'Failed to update license');
      }
    } catch (err) {
      showMessage('error', 'Fout bij bijwerken van licentie: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteLicense = async (license: License) => {
    if (!confirm(`Weet je zeker dat je de licentie voor ${license.username || license.email} wilt verwijderen?`)) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/licenses/${license.id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        showMessage('success', 'Licentie succesvol verwijderd');
        await fetchLicenses();
      } else {
        throw new Error('Failed to delete license');
      }
    } catch (error) {
      showMessage('error', 'Fout bij verwijderen van licentie');
    }
  };

  const openEditModal = (license: License) => {
    setEditingLicense(license);
    setEditForm({
      username: license.username || '',
      email: license.email || '',
      maxActivations: license.maxActivations,
      expiresAt: license.expiresAt ? formatDateForInput(license.expiresAt) : '',
      notes: license.notes || '',
      isActive: license.isActive
    });
    setShowEditModal(true);
  };

  const formatDateForInput = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      let date: Date;
      
      if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else {
        return '';
      }
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      return date.toISOString().slice(0, 16);
    } catch (error) {
      return '';
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [currentUser]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Nooit';
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp formats
      if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else {
        return 'Ongeldig';
      }
      
      if (isNaN(date.getTime())) {
        return 'Ongeldig';
      }
      
      // Use toLocaleString instead of toLocaleDateString to include time
      return date.toLocaleString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Ongeldig';
    }
  };

  const isExpired = (expiresAt: any) => {
    if (!expiresAt) return false;
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp formats
      if (expiresAt._seconds !== undefined) {
        date = new Date(expiresAt._seconds * 1000);
      } else if (expiresAt.seconds !== undefined) {
        date = new Date(expiresAt.seconds * 1000);
      } else if (expiresAt && typeof expiresAt === 'object' && expiresAt.seconds) {
        date = new Date(expiresAt.seconds * 1000);
      } else if (typeof expiresAt === 'number') {
        date = new Date(expiresAt);
      } else if (typeof expiresAt === 'string') {
        date = new Date(expiresAt);
      } else if (expiresAt instanceof Date) {
        date = expiresAt;
      } else if (expiresAt.toDate && typeof expiresAt.toDate === 'function') {
        date = expiresAt.toDate();
      } else {
        return false;
      }
      
      return date < new Date();
    } catch (error) {
      return false;
    }
  };

  const getStatusBadge = (license: License) => {
    if (!license.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Inactief
        </span>
      );
    }
    
    if (isExpired(license.expiresAt)) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Verlopen
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Actief
      </span>
    );
  };

  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = !filters.search || 
      license.username?.toLowerCase().includes(filters.search.toLowerCase()) ||
      license.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      license.licenseKey.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = !filters.status || 
      license.isActive.toString() === filters.status;
    
    const matchesExpired = !filters.expired || 
      isExpired(license.expiresAt).toString() === filters.expired;
    
    return matchesSearch && matchesStatus && matchesExpired;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Licenties laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            Licentie Beheer
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Maak en beheer software licenties
          </p>
          <p className="text-sm text-gray-500">
            Overzicht van alle licenties en hun status
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
          <button
            onClick={fetchLicenses}
            className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg border border-gray-200 hover:shadow-xl"
          >
            <span>🔄</span>
            <span>Vernieuwen</span>
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            <span>📧</span>
            <span>Uitnodiging Versturen</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            <span>➕</span>
            <span>Licentie Aanmaken</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🔍</span>
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Zoeken</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Zoek op gebruikersnaam, email of licentie sleutel..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="">Alle Status</option>
                  <option value="true">Actief</option>
                  <option value="false">Inactief</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vervaldatum</label>
                <select
                  value={filters.expired}
                  onChange={(e) => setFilters(prev => ({ ...prev, expired: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="">Alle</option>
                  <option value="false">Geldig</option>
                  <option value="true">Verlopen</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p className="text-sm text-gray-600">
                Toont {filteredLicenses.length} van {licenses.length} licenties
              </p>
              <button
                onClick={() => setFilters({ search: '', status: '', expired: '' })}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
              >
                Filters Wissen
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border shadow-sm ${
            message.type === 'success' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800' 
              : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
              <button
                onClick={() => setMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex">
              <span className="text-red-400">❌</span>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Fout</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Licenses Cards - Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLicenses.length === 0 ? (
            <div className="col-span-full">
              <div className="bg-white rounded-2xl shadow-2xl p-12 border border-gray-100 text-center">
                <span className="text-6xl mb-4 block">📄</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Geen licenties gevonden</h3>
                <p className="text-gray-600">Maak je eerste licentie aan of pas je filters aan</p>
              </div>
            </div>
          ) : (
            filteredLicenses.map((license) => (
              <div key={license.id} className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-12 translate-x-12 opacity-20"></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {license.username || 'Niet toegewezen'}
                      </h3>
                      <p className="text-sm text-gray-600">{license.email}</p>
                    </div>
                    {getStatusBadge(license)}
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Licentie Sleutel</p>
                      <p className="text-sm font-mono bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                        {license.licenseKey}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Activaties</p>
                        <p className="text-sm font-medium text-gray-900">
                          {license.activeActivations}/{license.maxActivations}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Totaal</p>
                        <p className="text-sm font-medium text-gray-900">
                          {license.totalActivations}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Aangemaakt</p>
                      <p className="text-sm text-gray-700">{formatDate(license.createdAt)}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Verloopt</p>
                      <p className="text-sm text-gray-700">{formatDate(license.expiresAt)}</p>
                    </div>
                    
                    {license.notes && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notities</p>
                        <p className="text-sm text-gray-700">{license.notes}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6 flex flex-wrap gap-2">
                    <button 
                      onClick={() => openEditModal(license)}
                      className="flex-1 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border border-blue-200"
                    >
                      ✏️ Bewerken
                    </button>
                    <button 
                      onClick={() => handleDeleteLicense(license)}
                      className="flex-1 bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 text-red-700 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border border-red-200"
                    >
                      🗑️ Verwijderen
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e
        </p>
      </div>

      {/* Create License Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">➕</span>
                Nieuwe Licentie Aanmaken
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Gebruikersnaam (Optioneel)</label>
                  <input
                    type="text"
                    value={createForm.username}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer gebruikersnaam in (laat leeg voor niet-toegewezen licentie)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Laat leeg om een niet-toegewezen licentie aan te maken
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer email adres in"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Maximaal Aantal Activaties</label>
                  <input
                    type="number"
                    min="1"
                    value={createForm.maxActivations}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, maxActivations: parseInt(e.target.value) || 1 }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Vervaldatum (optioneel)</label>
                  <input
                    type="datetime-local"
                    value={createForm.expiresAt}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Notities</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Optionele notities"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={createLicense}
                  disabled={creating}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl"
                >
                  {creating ? 'Aanmaken...' : 'Licentie Aanmaken'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📧</span>
                Licentie Uitnodiging Versturen
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Verstuur een email uitnodiging om een account aan te maken en een licentie te claimen. De ontvanger krijgt een email met een registratie link.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email Adres *</label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Voer email adres van ontvanger in"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Maximaal Aantal Activaties</label>
                  <input
                    type="number"
                    min="1"
                    value={inviteForm.maxActivations}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, maxActivations: parseInt(e.target.value) || 1 }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Vervaldatum (optioneel)</label>
                  <input
                    type="datetime-local"
                    value={inviteForm.expiresAt}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Notities</label>
                  <textarea
                    value={inviteForm.notes}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows={3}
                    placeholder="Optionele notities over deze licentie"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-4">
                <div className="flex items-start">
                  <span className="text-blue-400 mr-2">ℹ️</span>
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold">Hoe het werkt:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Een licentie wordt aangemaakt en gekoppeld aan dit email</li>
                      <li>Een uitnodiging email wordt automatisch verstuurd</li>
                      <li>De ontvanger kan registreren met hun gekozen gebruikersnaam/wachtwoord</li>
                      <li>De licentie wordt automatisch toegewezen bij registratie</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={createInvitation}
                  disabled={inviting || !inviteForm.email.trim()}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <span>📧</span>
                  <span>{inviting ? 'Versturen...' : 'Uitnodiging Versturen'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit License Modal */}
      {showEditModal && editingLicense && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">✏️</span>
                Licentie Bewerken
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Gebruikersnaam (Optioneel)</label>
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer gebruikersnaam in (laat leeg voor niet-toegewezen licentie)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Laat leeg om licentie niet-toegewezen te houden
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer email adres in"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Maximaal Aantal Activaties</label>
                  <input
                    type="number"
                    min="1"
                    value={editForm.maxActivations}
                    onChange={(e) => setEditForm(prev => ({ ...prev, maxActivations: parseInt(e.target.value) || 1 }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Vervaldatum (optioneel)</label>
                  <input
                    type="datetime-local"
                    value={editForm.expiresAt}
                    onChange={(e) => setEditForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Notities</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Optionele notities"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm font-semibold text-gray-900">
                    Licentie is actief
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLicense(null);
                  }}
                  disabled={updating}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={updateLicense}
                  disabled={updating}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl"
                >
                  {updating ? 'Bijwerken...' : 'Licentie Bijwerken'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Licenses; 